import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../locale/useTranslation";
import { ENTITYNAME, TOAST_TYPES } from "../../framework/constant/constant";
import * as API from "../../framework/API/api";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import ActionButton from "../../framework/forms/helpercomponents/buttons/ActionButton";
import ToastNotification from "../../components/ToastNotification";

const ITXMLParserEditForm = () => {

    const lang = useTranslation();
    const fileRef = useRef(null);

    const blankDataItem = { Channel: {} };
    const [dataItem, setDataItem] = useState(blankDataItem);
    const [fileName, setFileName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState("");
    const [channels, setChannels] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => { loadCombo(); }, []);

    const loadCombo = async () => {
        const channelRes = await API.getDataLookup(ENTITYNAME.Channel, { sort: { FullChannelName: 1 } });

        //FILTER CHANNELS ( KEEP ROCK ACTION CHANNEL )
        let filteredChannels = channelRes.data.filter((x) => x.SID == 2 || x.SID == 6);
        setChannels(filteredChannels);
        setDataItem({ ...blankDataItem, Channel: filteredChannels?.[0] ?? {} });

    };

    const onChange = (e) => {
        setDataItem({ ...dataItem, [e.target.name]: e.target.value });
    };

    const openFileDialog = () => { fileRef.current.click(); };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name) { setError("Invalid file"); return; }

        const extension = file.name.split('.').pop()?.toLowerCase();
        if (extension !== "itxml") {
            setError("Only ITXML files allowed");
            setFileName("");
            setSelectedFile(null);
            e.target.value = "";
            return;
        }

        setError("");
        setFileName(file.name);
        setSelectedFile(file);
    };

    const handleClearFile = () => {
        setFileName("");
        setSelectedFile(null);
        setError("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleConvert = async () => {
        if (!dataItem.Channel || !dataItem.Channel.SID) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Please select a channel first" });
            return;
        }
        if (!selectedFile) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Please select an ITXML file" });
            return;
        }
        setError("");
        setIsProcessing(true);
        try {
            const res = await API.uploadAndProcessITXML(selectedFile, dataItem.Channel.SID);
            if (res.success) {
                ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title, message: "File processed & downloaded successfully" });
                API.downloadFile(res.data);
            } else {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Error processing file: " + res.message });
            }
        } catch (e) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Unexpected error: " + e.message });
        } finally {
            setIsProcessing(false);
        }
    };

    /* ── inline styles ── */
    const styles = {
        wrapper: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 100px)",
            padding: "10px 16px",
        },
        card: {
            background: "#fff",
            borderRadius: "10px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            padding: "28px 32px",
            minWidth: "620px",
            maxWidth: "900px",
        },
        heading: {
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "22px",
            paddingBottom: "12px",
            borderBottom: "2px solid #f0f0f0",
        },
        headingIcon: {
            fontSize: "22px",
            color: "#4e73df",
        },
        headingText: {
            fontSize: "18px",
            fontWeight: 600,
            color: "#2d3748",
            margin: 0,
        },
        sectionLabel: {
            fontSize: "13px",
            fontWeight: 600,
            color: "#4a5568",
            marginBottom: "6px",
            display: "block",
        },
        stepBadge: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            background: "#4e73df",
            color: "#fff",
            fontSize: "12px",
            fontWeight: 700,
            marginRight: "8px",
            flexShrink: 0,
        },
        stepRow: {
            display: "flex",
            alignItems: "flex-start",
            gap: "0",
            marginBottom: "20px",
        },
        stepContent: {
            flex: 1,
        },
        fileRow: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
        },
        fileInput: {
            flex: 1,
            borderRadius: "6px",
            border: "1.5px solid #cbd5e0",
            padding: "7px 12px",
            fontSize: "13px",
            background: "#f7fafc",
            color: "#2d3748",
            outline: "none",
            cursor: "default",
        },
        browseBtn: {
            borderRadius: "6px",
            border: "1.5px solid #4e73df",
            background: "#4e73df",
            color: "#fff",
            padding: "7px 14px",
            fontSize: "13px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
            transition: "background 0.2s ease",
        },
        clearBtn: {
            borderRadius: "6px",
            border: "1.5px solid #e53e3e",
            background: "transparent",
            color: "#e53e3e",
            padding: "7px 10px",
            fontSize: "13px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            transition: "background 0.2s ease",
        },
        divider: {
            border: "none",
            borderTop: "1px dashed #e2e8f0",
            margin: "4px 0 20px 0",
        },
        errorBox: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "#fff5f5",
            border: "1px solid #fed7d7",
            borderRadius: "6px",
            padding: "8px 14px",
            color: "#c53030",
            fontSize: "13px",
            marginTop: "12px",
        },
        footer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingTop: "14px",
            borderTop: "2px solid #f0f0f0",
            marginTop: "4px",
        },
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.card}>

                {/* ── Header ── */}
                <div style={styles.heading}>
                    <i className="fa fa-file-code-o" style={styles.headingIcon} />
                    <h2 style={styles.headingText}>{lang.itxml_parser_dialog_header ?? "ITXML Parser"}</h2>
                </div>

                {/* ── Step 1 : Channel ── */}
                <div style={styles.stepRow}>
                    <span style={styles.stepBadge}>1</span>
                    <div style={styles.stepContent}>
                        <label style={styles.sectionLabel}>{lang.channel_label ?? "Channel"} *</label>
                        <DropDownList
                            style={{ width: "100%", maxWidth: "320px" }}
                            data={channels}
                            name="Channel"
                            textField="FullChannelName"
                            dataItemKey="_id"
                            onChange={onChange}
                            value={dataItem.Channel || null}
                        />
                    </div>
                </div>

                {/* ── Step 2 : File ── */}
                <div style={styles.stepRow}>
                    <span style={styles.stepBadge}>2</span>
                    <div style={styles.stepContent}>
                        <label style={styles.sectionLabel}>{lang.select_itxml_file_label} *</label>
                        <div style={styles.fileRow}>
                            <input
                                type="text"
                                style={styles.fileInput}
                                value={fileName}
                                placeholder="No file chosen"
                                readOnly
                            />
                            <input
                                type="file"
                                ref={fileRef}
                                style={{ display: "none" }}
                                accept=".itxml,application/xml,text/xml"
                                onChange={handleFileChange}
                            />
                            <button
                                type="button"
                                style={styles.browseBtn}
                                onClick={openFileDialog}
                                title="Browse for ITXML file"
                            >
                                <i className="fa fa-folder-open" />{lang.browser_button_text}
                            </button>
                            {selectedFile && (
                                <button
                                    type="button"
                                    style={styles.clearBtn}
                                    onClick={handleClearFile}
                                    title="Remove selected file"
                                >
                                    <i className="fa fa-times" />
                                </button>
                            )}
                        </div>
                        {selectedFile && (
                            <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#38a169" }}>
                                <i className="fa fa-check-circle" />
                                <span>{lang.file_ready_label}: <strong>{fileName}</strong></span>
                            </div>
                        )}
                    </div>
                </div>

                <hr style={styles.divider} />

                {/* ── Error ── */}
                {error && (
                    <div style={styles.errorBox}>
                        <i className="fa fa-exclamation-triangle" />
                        <span>{error}</span>
                    </div>
                )}

                {/* ── Footer / Action ── */}
                <div style={styles.footer}>
                    <ActionButton
                        name={`${lang.convert_button_label ?? "Convert"} & ${lang.download_label ?? "Download"}`}
                        title="Process and download the ITXML file"
                        icon="download"
                        onClick={handleConvert}
                        isLoading={isProcessing}
                    />
                </div>
            </div>
        </div>
    );
};

export default ITXMLParserEditForm;